import React, { CSSProperties, useEffect, useMemo, useState } from 'react'
import { Drawer, Tooltip } from 'antd'
import { AiButton } from '../ai-button/AiButton'
import { useAiChat } from '../ai-provider/AiProvider'
import { IconAiUser } from '../../icons/IconAiUser'
import { AiChat } from '../ai-chat/AiChat'
import { IconButton } from '../../buttons/icon-button/IconButton'
import { IconTrash } from '../../icons/IconTrash'
import { IconMinimize } from '../../icons/IconMinimize'
import { IconMaximize } from '../../icons/IconMaximize'
import { IconCross } from '../../icons/IconCross'
import { AiOverview } from '../ai-overview/AiOverview'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { selectUser } from '../../../../app/store/reducers/AuthSlice'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

type PopupMessage = React.PropsWithChildren

const PopupMessage: React.FC<PopupMessage> = ({ children }) => {
  return (
    <div className={'flex flex-col gap-[6px]'}>
      <div className={'flex items-center gap-[8px] font-medium'}>
        <div className={'h-[25px] w-[25px] rounded-full bg-[#E8EFFA] p-[5px] pl-[6px]'}>
          <div className={'w-[22px]'}>
            <IconAiUser color={'#00326D'} />
          </div>
        </div>
        Assistant
      </div>
      <p className='line-clamp-2'>{children}</p>
    </div>
  )
}

// Отступ кнопки от края окна, когда слота нет или он ниже экрана
const BUTTON_OFFSET = 20

// Ставит кнопку по слоту: правый край — по правому краю слота, центр — по центру кнопок
// перелистывания; пока слот ниже экрана, кнопка остаётся у нижнего края окна.
// Слот двигается и без скролла окна (прокрутка контейнера, подгрузка данных), поэтому
// положение сверяем каждый кадр, а стейт обновляем только при изменении
const useSlotPosition = (slot: HTMLElement | null, button: HTMLElement | null) => {
  const [position, setPosition] = useState<CSSProperties>()

  useEffect(() => {
    if (!slot || !button) {
      setPosition(undefined)
      return
    }

    let frameId: number

    const update = () => {
      const slotRect = slot.getBoundingClientRect()
      const anchorRect = (
        slot.querySelector('[data-ai-button-anchor]') ?? slot
      ).getBoundingClientRect()
      const right = Math.round(document.documentElement.clientWidth - slotRect.right)
      const anchorCenter = anchorRect.top + anchorRect.height / 2
      // где окажется кнопка, когда строка встанет вплотную к низу окна (у экранов с таблицей нет
      // отступа снизу): с нижней границей BUTTON_OFFSET она бы до кнопок перелистывания не доставала
      const flushBottom = slotRect.bottom - anchorCenter - button.offsetHeight / 2
      const bottom = Math.round(
        Math.max(
          Math.min(BUTTON_OFFSET, flushBottom),
          window.innerHeight - anchorCenter - button.offsetHeight / 2,
        ),
      )

      setPosition((prev) =>
        prev?.right === right && prev?.bottom === bottom ? prev : { right, bottom },
      )
      frameId = requestAnimationFrame(update)
    }

    update()

    return () => cancelAnimationFrame(frameId)
  }, [slot, button])

  return position
}

export const AiDrawer = () => {
  const { t } = useTranslation()
  const user = useAppSelector(selectUser)
  const {
    size,
    tab,
    setTab,
    messages,
    buttonVisible,
    open,
    toggleDrawer,
    toggleSize,
    clearChat,
    popupMessage,
    indicator,
    buttonSlot,
    setButtonWidth,
  } = useAiChat()
  const [button, setButton] = useState<HTMLDivElement | null>(null)
  const position = useSlotPosition(buttonSlot, button)

  // ширину кнопки отдаём слоту под резерв: подпись после локализации может быть длиннее
  useEffect(() => {
    if (!button) return

    const observer = new ResizeObserver(() => setButtonWidth(button.offsetWidth))

    observer.observe(button)

    return () => observer.disconnect()
  }, [button])

  const lastMessage = useMemo(() => messages[messages.length - 1].text, [messages])

  return (
    <div>
      <div
        ref={setButton}
        className={clsx(
          'fixed bottom-[13px] right-[16px] z-50 xl:right-[32px]',
          'transition-[transform,opacity] duration-300 ease-out',
          buttonVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        style={position}
      >
        <Tooltip
          getPopupContainer={(triggerNode) => triggerNode.parentElement!}
          open={popupMessage}
          placement={'topRight'}
          title={<PopupMessage>{lastMessage}</PopupMessage>}
        >
          <AiButton indicator={indicator} onClick={toggleDrawer}>
            {t('buttons.askAssistant')}
          </AiButton>
        </Tooltip>
      </div>

      <Drawer closable={false} mask={false} open={open} size={size} onClose={toggleDrawer}>
        <div className='flex h-full flex-col'>
          <div className='flex items-center justify-between border-b-[0.5px] border-b-[#0E63D8]/[0.25] py-[16px] px-[24px]'>
            {
              {
                chat: (
                  <div className={'flex items-center gap-[8px]'}>
                    <p className='text-[24px] text-focus-600'>AI Assistant</p>
                    <div
                      className={
                        'rounded-[6px] bg-[#E8EFFA] px-[8px] py-[2px] text-[10px] font-semibold uppercase leading-[16px] text-aqua-300'
                      }
                    >
                      Beta
                    </div>
                  </div>
                ),
                overview: (
                  <div className={'flex items-center gap-[8px]'}>
                    <p className='text-[24px] text-focus-600'>Overview</p>
                  </div>
                ),
              }[tab]
            }
            <div className='flex gap-[4px]'>
              {
                {
                  chat: (
                    <IconButton size='lg' onClick={clearChat}>
                      <IconTrash color='#00326D' />
                    </IconButton>
                  ),
                  overview: null,
                }[tab]
              }
              <IconButton size='lg' onClick={toggleSize}>
                {size === 'large' ? (
                  <IconMinimize color='#00326D' />
                ) : (
                  <IconMaximize color='#00326D' />
                )}
              </IconButton>
              <IconButton size='lg' onClick={toggleDrawer}>
                <IconCross color='#00326D' />
              </IconButton>
            </div>
          </div>
          {(user.role === 'admin' || user.is_virtual_mode) && (
            <div className={'flex gap-[17px] border-b-[0.5px] border-b-[#0E63D8]/[0.25] px-[24px]'}>
              <button className={'h-[32px]'} onClick={() => setTab('chat')}>
                AI Chat
              </button>
              <button className={'h-[32px]'} onClick={() => setTab('overview')}>
                Reports
              </button>
            </div>
          )}
          {{ chat: <AiChat />, overview: <AiOverview /> }[tab]}
        </div>
      </Drawer>
    </div>
  )
}
